import { zFightingFixDistance } from "@dextall/panels-defaults";
import customPanelsColorFactory from "../colors/customComponentColorsFactory";
import { claddingCellGap, editorCladdingGeometryThickness } from "../defaults";
import { CardinalDirection } from "../geometry/compass";
import { FacadeElementType } from "./facadeElementType";
import type { ICustomCornerSource } from "../../../responses/customCornerSource";
import type { IModelCorner } from "../../../responses/modelCorner";
import type { ExtrusionGeometry } from "../geometry/claddingCellExtrusionGeometryFactory";
import type { CornerCladdingCellGeometry } from "../geometry/cornerCladdingCellGeometry";
import type { HooksCoordinateSystems } from "./hooksCoordinateSystems";
import type { Depth, InstallationSequence, PanelParameters } from "./panelParameters";

export class CustomCorner {
    private readonly material = customPanelsColorFactory.getDefaultCornerMaterial();
    private readonly _depth: Depth;
    private _installationSequence: InstallationSequence;

    constructor(
        private readonly cornerSource: ICustomCornerSource,
        private readonly modelCorner: IModelCorner,
        private readonly hooksCoordinateSystem: HooksCoordinateSystems,
        private readonly cornerCladdingCellGeometry: CornerCladdingCellGeometry,
        private readonly modelOffset: THREE.Vector3,
        public readonly panelDbId: number,
        public readonly leftWingFacadeOrientation: CardinalDirection,
        public readonly rightWingFacadeOrientation: CardinalDirection,
        public readonly internalId?: string,
    ) {
        this._installationSequence = cornerSource.installationSequence;
        this._depth = cornerSource.depth;
        // this id is used to keep original corner id after undo or redo actions with corner
        this.internalId = internalId || cornerSource.id;
    }

    get id(): string {
        return this.cornerSource.id;
    }

    get elementType(): FacadeElementType.CustomCorner {
        return FacadeElementType.CustomCorner;
    }

    get installationSequence(): InstallationSequence {
        return this._installationSequence;
    }

    get depth(): Depth {
        return this._depth;
    }

    get customPanelTypeId(): string {
        return this.cornerSource.customCornerTypeId;
    }

    get wallCornerId(): string {
        return this.cornerSource.wallCornerId;
    }

    get userUniqueId(): number {
        return this.cornerSource.userUniqueId;
    }

    get heightIndex(): number {
        return this.cornerSource.heightIndex;
    }

    get offset(): number {
        return this.cornerSource.offset;
    }

    get leftWing(): number {
        return this.cornerSource.leftWing;
    }

    get rightWing(): number {
        return this.cornerSource.rightWing;
    }

    get height(): number {
        return this.cornerSource.height;
    }

    get angle(): number {
        return this.cornerSource.angle;
    }

    get elementName(): string {
        return this.cornerSource.elementName;
    }

    createGeometry(): ExtrusionGeometry[] {
        const origin = this.computeGeometryOrigin(tempVector);

        return this.cornerCladdingCellGeometry.createGeometry(origin, this.material, this.material);
    }

    getCornerCoordinateSystem(): THREE.Matrix4 {
        const origin = this.computeGeometryOrigin(tempVector);

        return this.cornerCladdingCellGeometry.getCornerCoordinateSystem(origin);
    }

    getLeftWingWallCoordinateSystem(): THREE.Matrix4 | undefined {
        return this.hooksCoordinateSystem.getCornerWingTransform(this, true);
    }

    getRightWingWallCoordinateSystem(): THREE.Matrix4 | undefined {
        return this.hooksCoordinateSystem.getCornerWingTransform(this, false);
    }

    computeTitleMatrix(titleSize: THREE.Vector3, forLeftWing: boolean): THREE.Matrix4 {
        const wingMatrix = this.hooksCoordinateSystem.getCornerWingTransform(this, forLeftWing)!;

        const wingLength = forLeftWing ? this.leftWing : this.rightWing;

        const position = new THREE.Vector3(
            0.5 * (wingLength - titleSize.x),
            0.5 * (this.height - titleSize.y),
            editorCladdingGeometryThickness + zFightingFixDistance,
        ).applyMatrix4(wingMatrix);

        return wingMatrix.setPosition(position);
    }

    setPanelType(targetPanelTypeId: string | { id: string }) {
        this.cornerSource.customCornerTypeId = typeof targetPanelTypeId === "string"
            ? targetPanelTypeId
            : targetPanelTypeId.id;
    }

    setPanelWindowDesignParameters() {
        // No windows in custom corners
        console.error("Custom corner panels do not have windows!");
    }

    setLouvreCutouts() {
        // No louvres in custom corners
        console.error("Custom corner panels do not have louvres!");
    }

    update({ elementName, installationSequence }: PanelParameters) {
        this.cornerSource.elementName = elementName;
        this._installationSequence = installationSequence;
    }

    setOffset(offset: number) {
        this.cornerSource.offset = offset;
    }

    getGeometryCenter(targetVector?: THREE.Vector3): THREE.Vector3 {
        const origin = this.computeGeometryOrigin(targetVector, false);

        const centerOffset = new THREE.Vector3()
            .addVectors(this.modelCorner.left.wallOrientation, this.modelCorner.right.wallOrientation)
            .normalize()
            .multiplyScalar(0.5 * editorCladdingGeometryThickness);

        return origin.add(centerOffset);
    }

    getTargetOffsetFor(worldPoint: THREE.Vector3): number {
        const cornerOrigin = this.computeWallCornerOrigin(tempVector);

        return worldPoint.z - cornerOrigin.z;
    }

    getWorldOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        return this.computeGeometryOrigin(targetVector, false);
    }

    private computeGeometryOrigin(targetVector?: THREE.Vector3, addCladdingCellGap = true): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        this.computeWallCornerOrigin(targetVector);

        targetVector.z += this.offset + (addCladdingCellGap ? claddingCellGap : 0);

        return targetVector;
    }

    private computeWallCornerOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        targetVector
            .copy(this.modelOffset)
            .add(this.modelCorner.origin);

        targetVector.z += this.modelCorner.heights[this.heightIndex];

        return targetVector;
    }
}

const tempVector = new THREE.Vector3();
